import length from "@turf/length";
import { useMemo, useRef } from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { legProgress } from "../reelAltitudes";
import type { ReelSegment } from "../TikTokVideoVanilla";
import { LEG_FINAL_PHASE, legPhaseState, writeLegLine, writeTransportMarker } from "./legWrites";
import { writeStopMarkerAtEnd } from "./markerWrites";

/**
 * What the map looks like after the sequences that came before the current frame -- in a tab that
 * did not run them.
 *
 * The route lines, the transport marker and the stops' pins are Mapbox state that only PathSequence
 * and MarkerSequence write, and only while their own part of the film is on screen. In one tab
 * rendering the film in order that is fine: every sequence runs, leaves its layers behind and the
 * next one draws on top. In a tab that starts partway in, the earlier ones never ran, so the film's
 * second half came out without the route it had travelled, and a stop that was about to fade out
 * faded from the layout it was created in rather than the one its marker had left.
 *
 * This puts it back, from the segment layout (`reelSegments`) and the frame alone:
 *
 *  - Legs. Every leg that is over gets the line PathSequence leaves on its last frame, every leg still
 *    to come gets the state it was created in, and between legs the transport marker stands where the
 *    last leg ended.
 *  - Stops. Every stop whose marker is over gets the layout MarkerSequence leaves on its last frame:
 *    the circle's radius, the number's size, the name's offset, the pulse, the photo fan's tilt and
 *    size. A stop's opacities are another matter: they are put back too while nothing else owns them,
 *    from the end of its marker until the leg that leaves it starts (the stop stays on the map under
 *    its photos, and under the outro for the last one), and left alone from there on, because on that
 *    leg PathSequence's `fadeStop` takes them over -- it writes opacities only, which is exactly why
 *    the layout has to be right before it does. A stop further back is at opacity 0 by then, but not
 *    at the creation layout: its `text-size` is a layout property, Mapbox's label collision reads it
 *    whatever the paint opacity is, and these symbols do not ignore placement, so an old stop's size
 *    still decides whether a basemap label next to it is drawn.
 *
 * It writes in its render body, like the sequences, and it is rendered before them. A mounted
 * PathSequence or MarkerSequence therefore writes after it in the same React render and wins: this
 * only ever speaks for what nothing mounted is saying.
 *
 * In an ordinary sequential render it changes nothing. Every value it writes is already on the map:
 * the finished lines are what their PathSequences left, the lines still to come were created at
 * opacity 0, the marker is where the last leg left it and as hidden as the stop after it left it, and
 * a stop's layout is what its MarkerSequence left, which nothing writes again. Mapbox does not redo
 * anything for a paint or layout property set to the value it already has. The one write that is not
 * free for an equal value, the transport marker's GeoJSON source, is made once per leg rather than on
 * every frame.
 *
 * The values a stop is created with are the ones `fadeStop(stop, 0)` writes, so a stop AHEAD of the
 * frame needs nothing: it is at opacity 0 in the state it was created in, in either kind of tab.
 */

type FinalLegState = { inkStop: number; markerPosition: number[] };

/** The state PathSequence leaves on each leg's last frame, by leg index. Worked out once, not per frame. */
export const finalLegStates = (legPaths: any[] | null | undefined): (FinalLegState | null)[] =>
	(legPaths || []).map((path) => {
		if (!path?.geometry) return null;
		try {
			return legPhaseState(path, length(path), legProgress(LEG_FINAL_PHASE));
		} catch {
			// Geometry turf cannot measure: PathSequence would not have drawn this leg either.
			return null;
		}
	});

/** Which leg's end the transport marker's source was last set to by `syncFinishedSequences`. */
export type SyncedSource = { map: any; leg: number } | null;

export const syncFinishedSequences = (
	map: any,
	{
		frame,
		segments,
		reelData,
		finals,
		color,
		scale,
		fps,
		showPhotoCards = false,
		cardStagger = 5,
		cardStiffness = 260,
	}: {
		frame: number;
		segments: ReelSegment[];
		reelData: any[];
		finals: (FinalLegState | null)[];
		color: string;
		scale: number;
		fps: number;
		showPhotoCards?: boolean;
		cardStagger?: number;
		cardStiffness?: number;
	},
	synced: { current: SyncedSource }
) => {
	if (!map || !segments?.length) return;
	// A layer the style never got (a leg with no geometry) is nothing to draw, not an error.
	const guarded = (layerId: string, write: () => void) => {
		try {
			if (map.getLayer(layerId)) write();
		} catch {
			// Nothing to restore on a layer that is not there.
		}
	};

	let inLeg = false;
	let ended: ReelSegment | null = null;
	for (const segment of segments) {
		if (segment.kind !== "leg") continue;
		if (segment.to <= frame) {
			ended = segment;
			const done = finals?.[segment.index];
			if (done) guarded(`line-${segment.index}`, () => writeLegLine(map, segment.index, color, done.inkStop));
		} else if (segment.from > frame) {
			guarded(`line-${segment.index}`, () => map.setPaintProperty(`line-${segment.index}`, "line-opacity", 0));
		} else {
			// This leg is on screen: its PathSequence is mounted and speaks for it, line and marker both.
			inLeg = true;
		}
	}

	// The stops whose marker is over.
	for (const marker of segments) {
		if (marker.kind !== "marker" || marker.to > frame) continue;
		// The leg that follows this stop's marker (and its photos): the one that fades the stop out.
		const next = segments.find((s) => s.kind === "leg" && s.from >= marker.to);
		writeStopMarkerAtEnd(
			map,
			{ index: marker.index, markerDuration: marker.to - marker.from, fps, reelData, showPhotoCards, cardStagger, cardStiffness, scale },
			// Until that leg starts nothing else speaks for the stop's opacities; from then on PathSequence does.
			{ opacities: !next || frame < next.from }
		);
	}

	if (inLeg) {
		// PathSequence is about to move the marker's source itself, so whatever was set here no longer holds.
		synced.current = null;
		return;
	}
	// Not in a leg, and at least one is over: the marker stands where the last one ended.
	const final = ended ? finals?.[ended.index] : null;
	if (!ended || !final) return;
	const endedLeg: ReelSegment = ended;
	const alreadyThere = synced.current?.map === map && synced.current?.leg === endedLeg.index;
	guarded(`line-${endedLeg.index}`, () => {
		writeTransportMarker(map, {
			vehicle: reelData?.[endedLeg.index]?.vehicle,
			scale,
			position: alreadyThere ? undefined : final.markerPosition,
			opacity: 0,
		});
		synced.current = { map, leg: endedLeg.index };
	});
};

const FinishedSequences = ({
	map,
	reelSegments,
	reelData,
	legPaths,
	PATH_COLOR,
	scale,
	showPhotoCards,
	cardStagger,
	cardStiffness,
}: {
	map: any;
	reelSegments?: ReelSegment[] | null;
	reelData: any[];
	/** The geometry PathSequence is given for each leg, by destination index. */
	legPaths?: any[] | null;
	PATH_COLOR: string;
	scale: number;
	/** What MarkerSequence is given, because its last frame depends on them. */
	showPhotoCards?: boolean;
	cardStagger?: number;
	cardStiffness?: number;
}) => {
	// Outside any Sequence, so this is the film's own frame.
	const frame = useCurrentFrame();
	const { fps } = useVideoConfig();
	const finals = useMemo(() => finalLegStates(legPaths), [legPaths]);
	const synced = useRef<SyncedSource>(null);

	if (!map || !reelSegments?.length) return null;
	syncFinishedSequences(map, { frame, segments: reelSegments, reelData, finals, color: PATH_COLOR, scale, fps, showPhotoCards, cardStagger, cardStiffness }, synced);
	return null;
};

export default FinishedSequences;
